import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Text,
  Textarea,
} from '@musa.tech/design-system';
import { DatePickerField, formatLocalDate, parseLocalDate } from '@/components/collects/sob-demanda/DatePickerField';
import { formatPhoneNumber } from '@/lib/utils';
import {
  type OnDemandContract,
} from '@/integrations/agreements/client';
import {
  describeAdvance,
  describeMonthlyUsage,
  formatAddress,
  formatDay,
  generatorName,
} from '@/utils/onDemandRequestRules';
import {
  MAX_DATES,
  type RequestFormErrors,
  type RequestFormPerson,
  type RequestFormState,
} from './requestForm';
import { OnDemandPhotoField } from './OnDemandPhotoField';

export interface RequestDetailsStepProps {
  contract: OnDemandContract;
  form: RequestFormState;
  onChange: (form: RequestFormState) => void;
  minDate: string;
  /** Por que a data mais cedo é a terça (piso semanal); nulo quando não há piso agora. */
  weeklyNote?: string | null;
  /** Erros só aparecem depois da primeira tentativa de enviar. */
  errors: RequestFormErrors;
  /** Recusa do serviço (antecedência, limite, vigência): mensagem pronta para o usuário. */
  submitError: string | null;
  /** Envio de foto em andamento: a página segura o pedido até a foto terminar de subir. */
  onPhotoUploadingChange: (uploading: boolean) => void;
}

export const RequestDetailsStep: React.FC<RequestDetailsStepProps> = ({
  contract,
  form,
  onChange,
  minDate,
  weeklyNote,
  errors,
  submitError,
  onPhotoUploadingChange,
}) => {
  const update = (patch: Partial<RequestFormState>) => onChange({ ...form, ...patch });

  const updateDate = (index: number, patch: Partial<RequestFormState['dates'][number]>) =>
    update({ dates: form.dates.map((d, i) => (i === index ? { ...d, ...patch } : d)) });

  return (
    <div className="flex flex-col gap-6">
      <ContractSummary contract={contract} minDate={minDate} />

      {weeklyNote && (
        <Alert data-testid="weekly-floor-note">
          <AlertTitle>Datas a partir de terça-feira</AlertTitle>
          <AlertDescription>{weeklyNote}</AlertDescription>
        </Alert>
      )}

      {submitError && (
        <Alert variant="destructive" data-testid="request-submit-error">
          <AlertTitle>A solicitação não foi criada</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <section className="flex flex-col gap-3">
        <SectionTitle title="Quando coletar" description="Cada data vira uma solicitação para o transportador." />

        {form.dates.map((entry, index) => (
          <div key={index} className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[220px_auto]">
            <Field label={`Data ${form.dates.length > 1 ? index + 1 : ''}`.trim()}>
              <DatePickerField
                value={entry.date ? parseLocalDate(entry.date) : null}
                onChange={(date) => updateDate(index, { date: date ? formatLocalDate(date) : '' })}
                minDate={minDate}
                allowSunday
              />
            </Field>
            {form.dates.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remover data ${index + 1}`}
                onClick={() => update({ dates: form.dates.filter((_, i) => i !== index) })}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        ))}

        {errors.dates && <FieldError message={errors.dates} />}

        {form.dates.length < MAX_DATES && (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => update({ dates: [...form.dates, { date: '' }] })}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Adicionar data
            </Button>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle title="O que coletar" description="Quantidade de cada data, no acondicionamento do contrato." />
        <div className="max-w-[220px]">
          <Field label="Quantidade" htmlFor="on-demand-quantity" error={errors.quantity}>
            <Input
              id="on-demand-quantity"
              type="number"
              min={1}
              step={1}
              value={form.quantity}
              onChange={(e) => update({ quantity: e.target.value })}
            />
          </Field>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <SectionTitle title="Quem acompanha" description="Contato que o transportador procura no dia da coleta." />
        <PersonFields
          prefix="requester"
          person={form.requester}
          onChange={(requester) => update({ requester })}
          errors={{ name: errors.requesterName, phone: errors.requesterPhone, email: errors.requesterEmail }}
        />

        <div className="flex items-center gap-2">
          <Checkbox
            id="on-demand-has-companion"
            checked={form.hasCompanion}
            onCheckedChange={(checked) => update({ hasCompanion: checked === true })}
          />
          <Label htmlFor="on-demand-has-companion" className="cursor-pointer">
            <Text variant="body" size="small" className="text-neutral-800">
              Outra pessoa acompanha a coleta
            </Text>
          </Label>
        </div>

        {form.hasCompanion && (
          <PersonFields
            prefix="companion"
            person={form.companion}
            onChange={(companion) => update({ companion })}
            errors={{ name: errors.companionName, phone: errors.companionPhone, email: errors.companionEmail }}
          />
        )}
      </section>

      <OnDemandPhotoField
        service="collect"
        title="Fotos e observações"
        description="Opcional. Ajudam o transportador a se preparar."
        photoUrls={form.photoUrls}
        onChange={(photoUrls) => update({ photoUrls })}
        onUploadingChange={onPhotoUploadingChange}
      >
        <Field label="Observações" htmlFor="on-demand-observations">
          <Textarea
            id="on-demand-observations"
            rows={3}
            placeholder="Ex.: acesso pelo portão lateral, procurar a recepção"
            value={form.observations}
            onChange={(e) => update({ observations: e.target.value })}
          />
        </Field>
      </OnDemandPhotoField>
    </div>
  );
};

const ContractSummary: React.FC<{ contract: OnDemandContract; minDate: string }> = ({ contract, minDate }) => (
  <Card>
    <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
      <SummaryItem label="Estabelecimento" value={generatorName(contract.generator)} detail={formatAddress(contract.address)} />
      <SummaryItem label="Resíduo" value={contract.material.name ?? '—'} detail={contract.packaging.name ?? undefined} />
      <SummaryItem
        label="Regra do contrato"
        value={describeAdvance(contract.rule)}
        detail={`${describeMonthlyUsage(contract)} · datas a partir de ${formatDay(minDate)}`}
      />
    </CardContent>
  </Card>
);

const SummaryItem: React.FC<{ label: string; value: string; detail?: string }> = ({ label, value, detail }) => (
  <div className="flex flex-col gap-0.5">
    <Text variant="label" size="small" className="text-neutral-500">
      {label}
    </Text>
    <Text variant="body" size="small" className="font-medium text-neutral-900">
      {value}
    </Text>
    {detail && (
      <Text variant="body" size="small" className="text-neutral-500">
        {detail}
      </Text>
    )}
  </div>
);

const SectionTitle: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex flex-col gap-0.5">
    <Text as="h3" variant="label" size="medium" className="text-neutral-900">
      {title}
    </Text>
    <Text variant="body" size="small" className="text-neutral-500">
      {description}
    </Text>
  </div>
);

const Field: React.FC<{ label: string; htmlFor?: string; error?: string; children: React.ReactNode }> = ({
  label,
  htmlFor,
  error,
  children,
}) => (
  <div className="flex flex-col gap-1">
    <Label htmlFor={htmlFor}>
      <Text variant="label" size="small" className="text-neutral-700">
        {label}
      </Text>
    </Label>
    {children}
    {error && <FieldError message={error} />}
  </div>
);

const FieldError: React.FC<{ message: string }> = ({ message }) => (
  <Text variant="body" size="small" className="text-error-500" role="alert">
    {message}
  </Text>
);

const PersonFields: React.FC<{
  prefix: 'requester' | 'companion';
  person: RequestFormPerson;
  onChange: (person: RequestFormPerson) => void;
  errors: { name?: string; phone?: string; email?: string };
}> = ({ prefix, person, onChange, errors }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
    <Field label="Nome" htmlFor={`${prefix}-name`} error={errors.name}>
      <Input id={`${prefix}-name`} value={person.name} onChange={(e) => onChange({ ...person, name: e.target.value })} />
    </Field>
    <Field label="Telefone" htmlFor={`${prefix}-phone`} error={errors.phone}>
      <Input
        id={`${prefix}-phone`}
        type="tel"
        inputMode="tel"
        placeholder="(11) 91234-5678"
        value={formatPhoneNumber(person.phone)}
        onChange={(e) => onChange({ ...person, phone: formatPhoneNumber(e.target.value) })}
      />
    </Field>
    <Field label="E-mail (opcional)" htmlFor={`${prefix}-email`} error={errors.email}>
      <Input
        id={`${prefix}-email`}
        type="email"
        value={person.email}
        onChange={(e) => onChange({ ...person, email: e.target.value })}
      />
    </Field>
  </div>
);
